//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 04/09/2024
// Version       : 1
// Description   : Menú principal con los vehiculos a revisar
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import Navbar from "../../Navbar";
import { MaterialReactTable } from "material-react-table";

// TABLA EN ESPAÑOL
import { MRT_Localization_ES } from "material-react-table/locales/es";

import {
  Button,
  Box,
  Typography,
  ThemeProvider,
  createTheme,
  useTheme,
  LinearProgress,
} from "@mui/material";
import Badge from "@mui/material/Badge";

//ADJUNTAR ARCHIVOS
import { v4 as uuidv4 } from "uuid";

import TareasRevision from "./TareasRevision";
import HistorialRevision from "./HistorialRevision";

// ICONOS
import HistoryIcon from "@mui/icons-material/History";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { logEventos } from "../../../services/services";
function VehiculosRevision() {
  const globalTheme = useTheme();
  const [vehiculos, setVehiculos] = useState({});
  const [loading, setLoading] = useState(false); // Estado de carga

  // ADJUNTOS
  const [uuid_revision, setUuid_revision] = useState(uuidv4()); //ADJUNTAR ARCHIVOS
  const [newUuid, setNewUuid] = useState(""); //ADJUNTAR COMPROBANTE

  //*************************************************************************************************************************************/

  const obtenerVehiculos = () => {
    setLoading(true); // Mostrar indicador de carga
    fetch(UrlApi + "/vehiculos_marca_modelo")
      .then((data) => data.json())
      .then((data) => {
        // Obtener la fecha de hoy en formato DD/MM/YYYY
        const hoy = new Date().toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        const vehiculosConRegistro = data.map((vehiculo) => ({
          ...vehiculo,
          registroHoy: vehiculo.ultima_fecha === hoy, // Comparar con la fecha de última revisión
        }));
        setVehiculos(vehiculosConRegistro);
        setLoading(false); // Ocultar indicador de carga
      })
      .catch(() => setLoading(false)); // Ocultar indicador en caso de error
  };

  useEffect(() => {
    obtenerVehiculos();
  }, []);

  //*************************************************************************************************************************************/

  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);

  // Abrir diálogo al seleccionar un vehículo
  const handleOpenDialog = (vehiculo) => {
    // GENERAR UUID PARA ARCHIVOS ADJUNTOS
    setUuid_revision(uuidv4());
    setNewUuid(uuid_revision);

    setVehiculoSeleccionado(vehiculo);
    setDialogOpen(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setVehiculoSeleccionado(null);
  };

  // ----------------

  // Dialog historial
  const handleOpenHistorial = (vehiculo) => {
    setVehiculoSeleccionado(vehiculo);
    setHistorialOpen(true);
  };

  const handleCloseHistorial = () => {
    setHistorialOpen(false);
    setVehiculoSeleccionado(null);
  };

  //*************************************************************************************************************************************/

  const columns = [
    { accessorKey: "patente", header: "Patente", size: 100 },
    { accessorKey: "modelo", header: "Modelo", size: 100 },
    { accessorKey: "tipo", header: "Tipo", size: 80 },
  ];

  const customLocalization = {
    ...MRT_Localization_ES,
    body: {
      ...MRT_Localization_ES.body,
      noData: "No hay registros para mostrar",
    },
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true, // Mostrar la barra de búsqueda
    pagination: { pageIndex: 0, pageSize: 10 },
    grouping: ["tipo"],
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
      "& .MuiSvgIcon-root": {
        color: "white", // color de los íconos
      },
    },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { backgroundColor: "#ffffff" },
    variant: "outlined",
    size: "small",
  };

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default: "rgb(234, 239, 241)",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "1rem",
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.8rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  useEffect(() => {
    logEventos(
      "acceso al menú Revisión Vehículos",
      "Utilización de Vehículos",
      "Revisión Vehículos",
      localStorage.getItem("user")
    );
  }, []);

  return (
    <>
      <Navbar />
      <Box
        sx={{
          marginTop: "20px",
          marginBottom: "25px",
          marginLeft: "40px",
          marginRight: "40px",
        }}
      >
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={columns}
            data={vehiculos}
            enableGlobalFilter={true}
            enableToolbarInternalActions={true}
            initialState={initialState}
            positionActionsColumn="last"
            autoResetPageIndex={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={true}
            enableColumnFilters={false}
            enableHiding={false}
            enableGrouping={true}
            localization={customLocalization}
            enableRowSelection={false}
            enableRowActions={true}
            muiTableHeadCellProps={tableHeadCellProps}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            renderRowActions={({ row }) => (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={() => handleOpenDialog(row.original)}
                  sx={{ mr: 1 }}
                >
                  Ver Tareas
                </Button>
                {row.original.conteo_observaciones > 0 ? (
                  <Badge
                    badgeContent={row.original.conteo_observaciones}
                    onClick={() => handleOpenHistorial(row.original)} // Abro el historial (Mismo evento que el Button)
                    sx={{
                      cursor: "pointer", // Cambia el cursor a mano
                      "& .MuiBadge-badge": {
                        right: -3,
                        top: 8,
                        backgroundColor: "#D32F2F",
                        border: `2px solid`,
                        padding: "0 4px",
                        color: "white",
                      },
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      startIcon={<HistoryIcon />}
                      onClick={() => handleOpenHistorial(row.original)}
                    >
                      Historial
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    startIcon={<HistoryIcon />}
                    onClick={() => handleOpenHistorial(row.original)}
                    disabled={row.original.ultima_fecha === null}
                  >
                    Historial
                  </Button>
                )}
              </>
            )}
            renderTopToolbarCustomActions={() => (
              <Typography variant="poster" fontSize={26}>
                Revisión de Vehículos
              </Typography>
            )}
            muiTableBodyRowProps={({ table, row }) => {
              const rowIndex = table
                .getRowModel() //  Obtengo todas las filas visibles en el orden actual
                .rows.findIndex((r) => r.id === row.id); // Busca el índice real de la fila en la tabla
              return {
                sx: {
                  backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f0f0f0", // Alternar los colores correctamente, incluso si hay agrupaciones o filtrados
                },
              };
            }}
            // Aquí se muestra el LinearProgress mientras los datos están cargando
            renderLoadingOverlay={() => (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
            state={{ isLoading: loading }} // Propiedad para indicar si está cargando
          />
        </ThemeProvider>
      </Box>

      {dialogOpen && (
        <TareasRevision
          obtenerVehiculos={obtenerVehiculos}
          vehiculo={vehiculoSeleccionado} // Enviar el vehículo seleccionado
          registroHoy={vehiculoSeleccionado.registroHoy} // Pasar si tiene registro hoy
          // ADJUNTOS
          newUuid={newUuid}
          // ABRIR Y CERRAR DIALOG
          open={dialogOpen} // Estado si el diálogo está abierto
          onClose={handleCloseDialog} // Cerrar el diálogo
        />
      )}

      {historialOpen && (
        <HistorialRevision
          vehiculo={vehiculoSeleccionado}
          // ABRIR Y CERRAR DIALOG
          open={historialOpen}
          onClose={handleCloseHistorial}
        />
      )}
    </>
  );
}

export default VehiculosRevision;
